import React, { useState, useEffect } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import {
  Printer,
  Activity,
  Zap,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Clock,
  Droplets,
  Gauge,
  LayoutDashboard,
  ShieldCheck,
  Wrench,
  History,
  Waves,
  AlertOctagon,
  TrendingUp,
  RefreshCcw,
  EthernetPort,
} from "lucide-react";
import { TbBorderSides } from "react-icons/tb";
import { useSelector } from "react-redux";

// --- Real-time Monitoring Data Context ---
const DASHBOARD_DATA = {
  survey: {
    siteEngineer: "Rahul Sharma",
    hpRating: "75 HP (Pump 1), 75 HP (Pump 2)",
    plc: "Siemens S7-1200",
    mccbModel: "CZ-250-X1",
    earthingStatus: "Certified (< 2 Ohms)",
    particularsOfWork:
      "Retrofitting of IoT sensors and PLC integration for real-time monitoring.",
  },
  // Operations Dashboard KPIs
  operations: {
    kpis: {
      pumpsRunning: "03",
      activeAlarms: "01",
      avgFlow: "148.5 m³/h",
      avgPressure: "4.1 bar",
    },
    // Widget: Tank Levels point
    tankLevels: [
      { name: "Primary Sump", level: 82, status: "Stable" },
      { name: "Overhead Tank 01", level: 45, status: "Filling" },
      { name: "Auxiliary Tank", level: 12, status: "Critical" },
    ],
    // Widget: Alarm List point
    alarmList: [
      {
        id: "AL-77",
        asset: "Pump-02",
        msg: "High Vibration Detected",
        time: "14:05",
        type: "Warning",
      },
      {
        id: "AL-82",
        asset: "Flowmeter-S",
        msg: "Data Packet Loss",
        time: "13:50",
        type: "Critical",
      },
    ],
  },
  // Maintenance Module Points
  maintenance: {
    activeTickets: [
      {
        id: "MNT-2025-09",
        asset: "Pump-07",
        issueType: "Impeller Wear",
        priority: "High",
        vendor: "Apex Services",
        tenderId: "TND-IND-442",
        cost: "₹ 15,800",
        startDate: "2025-12-28",
        resolution: "Under Inspection",
        description:
          "Energy consumption increased by 35% per m³. Inspecting impeller alignment.",
      },
    ],
  },
  // Asset Status Table Widget
  assets: [
    {
      id: "A1",
      name: "Pump-01",
      type: "Tubewell",
      location: "T-30, Trikuta Nagar",
      status: "Active",
      energy: "Normal",
    },
    {
      id: "A2",
      name: "Flowmeter-N",
      type: "DN150",
      location: "South Zone",
      status: "Active",
      energy: "Optimized",
    },
    {
      id: "A3",
      name: "Pump-07",
      type: "Tubewell",
      location: "Zone B",
      status: "Maintenance",
      energy: "High Usage",
    },
  ],
  chartData: [
    { t: "10:00", f: 120, e: 90 },
    { t: "11:00", f: 140, e: 105 },
    { t: "12:00", f: 148, e: 110 },
    { t: "13:00", f: 130, e: 98 },
    { t: "14:00", f: 155, e: 115 },
    { t: "15:00", f: 145, e: 108 },
  ],
};

const OperationsReport = () => {
  const themeMode = useSelector((state) => state.theme.mode);
  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const borderThem = themeMode ? "border_them_2" : "border-primary";

  return (
    <div className={`w-full ${bgColorClass} ${textColorClass} h-full`}>
      {/* Top Header / Nav */}
      <div
        className={`py-4 md:px-12 px-6 border-b ${borderThem} flex justify-between items-center h-[57px]`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColorClass} items-center  gap-x-2`}
        >
          <p>
            <EthernetPort  size={16} />
          </p>
          <p className="font-semibold">Operations Dashboard</p>
        </div>
      </div>

      <div className=" h-[calc(100vh-50px)] pb-10 sidebar_scroll overflow-y-auto sidebar_scroll">
        <div className="px-3 md:px-5 lg:px-12 p-6 space-y-6">
          {/* Row 1: Operations Dashboard KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <LiveKPICard
              label="Pumps Running"
              value={DASHBOARD_DATA?.operations.kpis.pumpsRunning}
              sub="+1 since yesterday"
              icon={<Activity />}
              color="text-green-500"
            />
            <LiveKPICard
              label="Active Alarms"
              value={DASHBOARD_DATA?.operations.kpis.activeAlarms}
              sub="1 Critical Alert"
              icon={<AlertOctagon />}
              color="text-red-500"
            />
            <LiveKPICard
              label="Avg Flow"
              value={DASHBOARD_DATA?.operations.kpis.avgFlow}
              sub="Within normal range"
              icon={<Droplets />}
              color="text-blue-500"
            />
            <LiveKPICard
              label="Avg Pressure"
              value={DASHBOARD_DATA?.operations.kpis.avgPressure}
              sub="Optimal load"
              icon={<Gauge />}
              color="text-purple-500"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main Visual: Energy vs Flow Analytics */}
            <div
              className={`lg:col-span-8 ${bgColorClass} border ${borderThem} rounded-2xl p-3 shadow-sm`}
            >
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="font-semibold text-sm uppercase flex items-center gap-2 tracking-tight opacity-60">
                    System Performance Chart
                  </h3>
                  <p className="text-xs font-medium tracking-tighter">
                    Energy (kWh) vs Flow (m³/h) Correlation
                  </p>
                </div>
                <TrendingUp className="text-blue-500 opacity-20" size={30} />
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={DASHBOARD_DATA?.chartData}>
                    <defs>
                      <linearGradient id="colorF" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="#3b82f6"
                          stopOpacity={0.1}
                        />
                        <stop
                          offset="95%"
                          stopColor="#3b82f6"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      strokeOpacity={0.1}
                    />
                    <XAxis
                      dataKey="t"
                      fontSize={10}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis fontSize={10} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "12px",
                        border: "none",
                        boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="f"
                      stroke="#3b82f6"
                      fill="url(#colorF)"
                      strokeWidth={3}
                    />
                    <Area
                      type="monotone"
                      dataKey="e"
                      stroke="#f59e0b"
                      fill="transparent"
                      strokeDasharray="5 5"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Widget: Tank Levels point */}
            <div
              className={`lg:col-span-4 ${bgColorClass} border ${borderThem} rounded-2xl p-3 shadow-sm`}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold text-sm uppercase opacity-60 flex items-center gap-2">
                  <Waves size={14} /> Tank Levels Monitor
                </h3>
                <RefreshCcw size={12} className="opacity-30" />
              </div>
              <div className="space-y-6">
                {DASHBOARD_DATA?.operations.tankLevels.map((tank, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-[11px] font-semibold uppercase tracking-tight">
                        {tank.name}
                      </span>
                      <span
                        className={`text-[10px] font-semibold ${
                          tank.level < 20 ? "text-red-500" : "text-blue-500"
                        }`}
                      >
                        {tank.level}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200/50 h-2 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-1000 ${
                          tank.level < 20 ? "bg-red-500" : "bg-blue-500"
                        }`}
                        style={{ width: `${tank.level}%` }}
                      />
                    </div>
                    <div className="flex justify-between opacity-40 text-[8px] uppercase font-bold tracking-widest">
                      <span>Status: {tank.status}</span>
                      <span>Verified</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Widget: Asset Status Table point */}
            <div
              className={`lg:col-span-8 ${bgColorClass} border ${borderThem} rounded-2xl overflow-hidden shadow-sm`}
            >
              <div className="p-3 border-b flex justify-between items-center bg-slate-500/5">
                <h3 className="font-semibold text-sm uppercase tracking-widest opacity-60">
                  Asset Operational Status
                </h3>
              </div>
              <table className="w-full text-left">
                <thead className="bg-slate-500/5 text-[9px] uppercase font-black text-slate-500 border-b">
                  <tr>
                    <th className="p-4">Asset Detail</th>
                    <th className="p-4">Location</th>
                    <th className="p-4 text-center">Energy Load</th>
                    <th className="p-4 text-right">Operation</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {DASHBOARD_DATA?.assets.map((a, i) => (
                    <tr
                      key={i}
                      className="border-b border-slate-500/5 hover:bg-slate-500/5 transition-colors"
                    >
                      <td className="p-4">
                        <p className="font-semibold text-slate-800">{a.name}</p>
                        <p className="text-[10px] opacity-50 uppercase font-bold tracking-tight">
                          {a.type}
                        </p>
                      </td>
                      <td className="p-4 font-medium opacity-70 italic">
                        {a.location}
                      </td>
                      <td className="p-4 text-center">
                        <span
                          className={`text-[9px] font-semibold px-2 py-0.5 rounded ${
                            a.energy === "High Usage"
                              ? "bg-red-100 text-red-600"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {a.energy}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <span
                          className={`px-2 py-1 rounded-full text-[9px] font-black uppercase ${
                            a.status === "Active"
                              ? "bg-green-100 text-green-700"
                              : "bg-orange-100 text-orange-700"
                          }`}
                        >
                          ● {a.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Widget: Alarm List point */}
            <div
              className={`lg:col-span-4 ${bgColorClass} border ${borderThem} rounded-2xl p-3 shadow-sm`}
            >
              <h3 className="font-semibold text-sm uppercase mb-6 opacity-60 flex items-center gap-2 tracking-widest border-b pb-2">
                <AlertTriangle size={14} className="text-red-500" /> Active
                Alarm Stack
              </h3>
              <div className="space-y-4">
                {DASHBOARD_DATA?.operations.alarmList.map((alarm, i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-3 bg-red-500/5 rounded-xl border-l-2 border-red-500"
                  >
                    <Clock
                      size={16}
                      className="text-red-500 opacity-40 shrink-0"
                    />
                    <div className="space-y-1">
                      <div className="flex justify-between items-center w-full">
                        <span className="text-[10px] font-black text-red-600 uppercase leading-none">
                          {alarm.severity}
                        </span>
                        <span className="text-[8px] opacity-40 font-bold">
                          {alarm.time}
                        </span>
                      </div>
                      <p className="text-xs font-semibold leading-tight tracking-tight">
                        {alarm.asset}
                      </p>
                      <p className="text-[11px] opacity-70 leading-tight">
                        {alarm.msg}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Maintenance Module & Engineering Specs */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-20">
            <div
              className={`lg:col-span-6 ${bgColorClass} border ${borderThem} rounded-2xl p-3 shadow-sm`}
            >
              <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h3 className="font-semibold text-sm uppercase tracking-widest flex items-center gap-2">
                  <Wrench size={16} className="text-orange-500" /> Maintenance
                  Request Registry
                </h3>
                <div className="bg-orange-500/10 text-orange-600 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter">
                  System Health Check
                </div>
              </div>
              {DASHBOARD_DATA?.maintenance.activeTickets.map((m, i) => (
                <div
                  key={i}
                  className="grid grid-cols-2 gap-4 text-[11px] font-semibold"
                >
                  <div className="space-y-4">
                    <MaintenanceItem label="Ticket ID" value={m.id} />
                    <MaintenanceItem label="Issue Type" value={m.issueType} />
                    <MaintenanceItem label="Assigned Vendor" value={m.vendor} />
                    <MaintenanceItem label="Tender ID" value={m.tenderId} />
                  </div>
                  <div className="space-y-4">
                    <MaintenanceItem
                      label="Priority"
                      value={m.priority}
                      color="text-red-600"
                    />
                    <MaintenanceItem label="Start Date" value={m.startDate} />
                    <MaintenanceItem label="Budgeted Cost" value={m.cost} />
                    <MaintenanceItem
                      label="Status"
                      value={m.status}
                      color="text-blue-600"
                    />
                  </div>
                  <div className="col-span-2 pt-4 mt-4 border-t">
                    <p className="text-[9px] opacity-40 uppercase mb-1">
                      Issue Description
                    </p>
                    <p className="italic opacity-70 leading-relaxed">
                      "{m.description}"
                    </p>
                  </div>
                  {/* Images placeholders from requested point */}
                  <div className="col-span-2 grid grid-cols-2 gap-4 mt-4">
                    <div className="h-16 border border-dashed rounded-xl flex items-center justify-center text-[8px] opacity-30 text-center uppercase">
                      Image Before Repair
                    </div>
                    <div className="h-16 border border-dashed rounded-xl flex items-center justify-center text-[8px] opacity-30 text-center uppercase">
                      Image After Repair
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              className={`lg:col-span-6 ${bgColorClass} border ${borderThem} rounded-2xl p-3 shadow-sm`}
            >
              <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h3 className="font-semibold text-sm uppercase tracking-widest flex items-center gap-2">
                  <History size={16} className="text-blue-500" /> Engineering
                  Survey Context
                </h3>
                <RefreshCcw size={12} className="opacity-30" />
              </div>
              <div className="grid grid-cols-1 gap-2">
                <DataRow
                  label="Site Engineer"
                  value={DASHBOARD_DATA?.survey?.siteEngineer}
                  isRed
                />
                <DataRow
                  label="HP Rating"
                  value={DASHBOARD_DATA?.survey?.hpRating}
                  isBlue
                />
                <DataRow
                  label="PLC Controller"
                  value={DASHBOARD_DATA?.survey?.plc}
                />
                <DataRow
                  label="MCCB Model"
                  value={DASHBOARD_DATA?.survey?.mccbModel}
                  isRed
                />
                <DataRow
                  label="Grounding Status"
                  value={DASHBOARD_DATA?.survey?.earthingStatus}
                />
                <div className="mt-4 p-4 bg-red-500/5 rounded-xl border border-red-500/10">
                  <p className="text-[9px] font-black text-red-600 uppercase mb-1 underline underline-offset-4 tracking-widest">
                    Ongoing Work Insight
                  </p>
                  <p className="text-[11px] font-bold leading-relaxed opacity-80">
                    "{DASHBOARD_DATA?.survey?.particularsOfWork}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Absolute Bottom Notification (Mobile UI feel) */}
      <div className="fixed hidden bottom-4 left-1/2 -translate-x-1/2 w-[90%] max-w-lg bg-slate-900 text-white p-3 rounded-2xl shadow-2xl flex items-center justify-between border border-white/10 backdrop-blur-xl z-50 print:hidden">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-xl flex items-center justify-center">
            <Droplets size={16} />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-tighter text-blue-400 leading-none">
              Global Consumption
            </p>
            <p className="text-xs font-bold">
              Flow rate optimized for Morning Shift
            </p>
          </div>
        </div>
        <button className="text-[10px] font-black uppercase bg-white/10 px-3 py-1 rounded-lg">
          Details
        </button>
      </div>
    </div>
  );
};

// --- Reusable UI Sub-Components ---

const LiveKPICard = ({ label, value, sub, icon, trend, color }) => (
  <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm hover:shadow-md transition-shadow group relative overflow-hidden">
    <div className="flex justify-between items-start mb-4">
      <div className={`${color} bg-current/10 p-2.5 rounded-xl`}>{icon}</div>
      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping"></div>
    </div>
    <p className="text-xl font-black mb-1 tracking-tighter">{value}</p>
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">
      {label}
    </p>
    <p className="text-[9px] opacity-40 font-bold italic tracking-tight">
      {sub}
    </p>
  </div>
);

const MaintenanceItem = ({ label, value, color = "text-slate-900" }) => (
  <div className="border-b border-slate-100 pb-2">
    <p className="text-[9px] font-semibold uppercase opacity-40 leading-none mb-1">
      {label}
    </p>
    <p className={`text-[11px] font-semibold ${color} tracking-tight`}>{value}</p>
  </div>
);

const DataRow = ({ label, value, isRed, isBlue }) => (
  <div className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0 group">
    <span className="text-[10px] font-semibold opacity-30 uppercase tracking-tighter">
      {label}
    </span>
    <span
      className={`text-[11px] font-semibold tracking-tight ${
        isRed ? "text-red-600" : isBlue ? "text-blue-600" : ""
      }`}
    >
      {value}
    </span>
  </div>
);

export default OperationsReport;
